export type PaperSize = 'A4' | 'A3'
export type Orientation = 'portrait' | 'landscape'
export type PrintScale = 1 | 0.8 | 0.65
export type PrintZoom = 'fit' | 'day' | 'week' | 'month' | 'quarter'

export interface PrintOptions {
  paper: PaperSize
  orientation: Orientation
  scale: PrintScale
  zoom: PrintZoom
  includeBaseline: boolean
  includeLinks: boolean
  includeCritical: boolean
  includeLegend: boolean
  /** Grid columns on every timeline strip (default). Off: the grid prints on the first strip only and
   * every later strip gets the whole content width for the timeline (MS Project's "print left column of
   * pages only"). No effect on the single-strip fit zoom. */
  repeatColumns: boolean
}

export const PAPER_SIZES: PaperSize[] = ['A4', 'A3']
export const ORIENTATIONS: Orientation[] = ['landscape', 'portrait']
export const PRINT_SCALES: PrintScale[] = [1, 0.8, 0.65]
export const PRINT_ZOOMS: PrintZoom[] = ['fit', 'day', 'week', 'month', 'quarter']
export const STORAGE_KEY_PRINT = 'urkla.schedule.print'

export const DEFAULT_PRINT_OPTIONS: PrintOptions = {
  paper: 'A4', orientation: 'landscape', scale: 0.8, zoom: 'fit',
  includeBaseline: true, includeLinks: true, includeCritical: true, includeLegend: true, repeatColumns: true,
}

const oneOf = <T,>(list: T[], v: unknown, fallback: T): T => (list.indexOf(v as T) >= 0 ? (v as T) : fallback)
const bool = (v: unknown, fallback: boolean): boolean => (typeof v === 'boolean' ? v : fallback)

/** Every key validated on its own: a partial, stale or hand-edited stored object never poisons the rest (spec §2). */
export const sanitizePrintOptions = (raw: unknown): PrintOptions => {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const d = DEFAULT_PRINT_OPTIONS
  return {
    paper: oneOf(PAPER_SIZES, r.paper, d.paper),
    orientation: oneOf(ORIENTATIONS, r.orientation, d.orientation),
    scale: oneOf(PRINT_SCALES, r.scale, d.scale),
    zoom: oneOf(PRINT_ZOOMS, r.zoom, d.zoom),
    includeBaseline: bool(r.includeBaseline, d.includeBaseline),
    includeLinks: bool(r.includeLinks, d.includeLinks),
    includeCritical: bool(r.includeCritical, d.includeCritical),
    includeLegend: bool(r.includeLegend, d.includeLegend),
    repeatColumns: bool(r.repeatColumns, d.repeatColumns),
  }
}

export const loadPrintOptions = (): PrintOptions => {
  try { const raw = localStorage.getItem(STORAGE_KEY_PRINT); return sanitizePrintOptions(raw ? JSON.parse(raw) : null) } catch { return { ...DEFAULT_PRINT_OPTIONS } }
}
export const savePrintOptions = (o: PrintOptions): void => {
  try { localStorage.setItem(STORAGE_KEY_PRINT, JSON.stringify(o)) } catch { /* private mode */ }
}
